import { isEmpty, isNotEmpty, type AreaData, type LatLng, type TeamId } from '@terros/common'
import { useMemo } from 'react'
import { useTeams } from '../../team/list'
import { useAllAreas } from '../list'
import { getClipCandidates, getOverlappingAreas, isPolygonWithinBoundary, type AreaClipCandidate } from './geo'
import { useStrictSubareaAssignment } from './strictSubarea'

type ValidationStatus = 'valid' | 'no-boundary' | 'outside-boundary' | 'loading' | 'disabled'

export type StrictAreaValidation = {
  status: ValidationStatus
  clipCandidates: AreaClipCandidate[]
  parentAreas: AreaData[]
  overlappingSiblings: AreaData[]
  isBlocking: boolean
}

type Props = {
  coordinates: LatLng[] | undefined
  filterTeamIds: TeamId[] | undefined
}

export function useStrictAreaValidation({ coordinates, filterTeamIds }: Props): StrictAreaValidation {
  const { mode, isLoading: isModeLoading } = useStrictSubareaAssignment(filterTeamIds)
  const {
    data: allAreas,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isAreasLoading,
  } = useAllAreas({
    downlineTeamIds: filterTeamIds,
  })
  const { data: allTeams } = useTeams()

  return useMemo((): StrictAreaValidation => {
    const disabled: StrictAreaValidation = {
      status: 'disabled',
      clipCandidates: [],
      parentAreas: [],
      overlappingSiblings: [],
      isBlocking: false,
    }

    if (mode === 'off') return disabled
    if (isEmpty(filterTeamIds) || isEmpty(coordinates)) return disabled
    if (isModeLoading || isAreasLoading || hasNextPage || isFetchingNextPage) {
      return {
        status: 'loading',
        clipCandidates: [],
        parentAreas: [],
        overlappingSiblings: [],
        isBlocking: false,
      }
    }

    const parentAreas = (allAreas ?? []).filter(
      (a) =>
        filterTeamIds.some((teamId) => a.teamId === teamId) && isNotEmpty(a.coordinates) && a.coordinates.length >= 3
    )

    const childTeamIds = (allTeams ?? [])
      .filter((t) => filterTeamIds.some((ft) => ft === t.parentId))
      .map((t) => t.teamId)
    const childTeamIdSet = new Set(childTeamIds)

    const siblingAreas = (allAreas ?? []).filter(
      (a) => a.teamId && childTeamIdSet.has(a.teamId) && isNotEmpty(a.coordinates) && a.coordinates.length >= 3
    )

    const overlappingSiblings = getOverlappingAreas(coordinates, siblingAreas)

    if (isEmpty(parentAreas)) {
      return {
        status: 'no-boundary',
        clipCandidates: [],
        parentAreas,
        overlappingSiblings,
        isBlocking: false,
      }
    }

    const isWithin = isPolygonWithinBoundary(coordinates, parentAreas)
    if (!isWithin) {
      return {
        status: 'outside-boundary',
        clipCandidates: getClipCandidates(coordinates, parentAreas),
        parentAreas,
        overlappingSiblings,
        isBlocking: mode === 'block',
      }
    }

    return {
      status: 'valid',
      clipCandidates: [],
      parentAreas,
      overlappingSiblings,
      isBlocking: false,
    }
  }, [mode, filterTeamIds, coordinates, isModeLoading, isAreasLoading, hasNextPage, isFetchingNextPage, allAreas, allTeams])
}
